import { z } from 'zod'
import { InsiRechercheResultSchema, InsVerifiedVia } from '@/lib/Ameli/INSi/insiTypes'

/**
 * Schemas Zod de mutation INSi (validation des payloads REST, cf. regle 7 du
 * CLAUDE.md : toute mutation REST doit etre validee par Zod).
 */

/** Canal ayant fourni/verifie l'INS (vitale / traits / verification). */
export const insChannelSchema = z.enum([
  InsVerifiedVia.VITALE,
  InsVerifiedVia.TRAITS,
  InsVerifiedVia.VERIFICATION,
])
export type InsChannel = z.infer<typeof insChannelSchema>

/**
 * Payload de PATCH /care/dossier/{id}/ins.
 *
 * CONTRAT CANONIQUE : le corps porte `{ result, signature, channel, expiresAt, nonce }` (PAS un
 * objet plat insMatricule/insCle...). `result` est le payload metier renvoye par l'execute
 * (InsiResult::toArray), `signature` est le HMAC renvoye par l'execute ; l'API re-verifie ce
 * HMAC sur `result` (augmente de `channel` + `expiresAt`) avant de persister. `result`,
 * `channel` et `expiresAt` doivent donc etre transmis TELS QUELS (renvoyes par l'execute) :
 * les modifier fait echouer la verification (channel signe + TTL, findings A2/A3).
 *
 * `id` est le path variable (resolu par fetchRest, non serialise dans le corps).
 */
export const careDossierInsPatchSchema = z.object({
  id: z.number(),
  /** Payload metier InsiResult::toArray (forme a plat, signe par HMAC). */
  result: InsiRechercheResultSchema,
  /** HMAC du payload result, renvoye par l'execute (repropage tel quel). */
  signature: z.string().min(1),
  /** Canal signe cote serveur (vitale | traits | verification), repropage tel quel. */
  channel: insChannelSchema,
  /** Timestamp Unix d'expiration de la signature (TTL), repropage tel quel. */
  expiresAt: z.number().int().positive(),
  /**
   * Nonce a usage unique renvoye par l'execute, repropage tel quel.
   *
   * La signature est liee au dossier, au groupement, a l'utilisateur ET a ce nonce (point R004 de
   * la revue de securite) : sans lui la verification echoue, et il n'est consommable qu'une fois.
   */
  nonce: z.string().min(1),
  // Les traits de reference (TIQ) portes par `result.traitsReference` sont TOUJOURS persistes
  // cote API dans le bloc INS dedie du dossier, sans ecraser les traits vivants (carte/saisie).
})

export type CareDossierInsPatch = z.infer<typeof careDossierInsPatchSchema>
